import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@parrot-software/design-system';
import { ReceiptText } from '@parrot-software/design-system/icons';
import { useIsMobile } from '@parrot-software/hooks';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { usePaymentHistoryInfinite } from '@subscriptions/presentation/hooks';
import { EmptyState, ErrorState } from '@subscriptions/presentation/components/base';
import { BlockPendingPaymentAlert } from '@subscriptions/presentation/components/specific/BlockDetail/BlockPendingPaymentAlert';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { buildOverdueChargeRow, getInvoicingDeadlineLabel } from '@subscriptions/presentation/utils';
import {
  PaymentHistoryMobileList,
  PaymentHistoryMobileListSkeleton,
  PaymentHistoryTable,
  PaymentHistoryTableSkeleton,
} from '@subscriptions/presentation/components/specific/PaymentHistory';

interface BlockPaymentHistoryContentProps {
  block: SubscriptionBlockViewDto;
}

/**
 * BlockPaymentHistoryContent - Payment history tab for the block detail page.
 *
 * Reuses the shared PaymentHistoryTable filtered by blockUuid with infinite scroll.
 * Pagination cache is cleared on unmount to restart at page 1 on next entry.
 *
 * Drops the identity and concept columns, which repeat what the page already says.
 *
 * With no fiscal record, a note replaces the downloads the table will never offer. Its link into the
 * invoicing tab stays disabled for a read-only organization, which has that tab locked.
 *
 * A SPEI block that owes its charge gets both an alert and the charge itself as the first row.
 */
export const BlockPaymentHistoryContent = ({ block }: BlockPaymentHistoryContentProps) => {
  const { t } = useTranslation('paymentHistory');
  const isMobile = useIsMobile();
  const blockUuid = block.uuid;
  const hasInvoiceData = block.invoiceData !== null;
  const areActionsLocked = useAreSubscriptionActionsLocked();

  const {
    items,
    isLoading,
    isFetchingNextPage,
    isError,
    refetch,
    remove,
    hasNextPage,
    fetchNextPage,
  } = usePaymentHistoryInfinite({ blockUuid });

  // The payments list never carries the unsettled charge: the API drops PENDING rows.
  const overdueCharge = buildOverdueChargeRow(block, t('subscriptionChargeConcept'));
  const rows = overdueCharge ? [overdueCharge, ...items] : items;
  const hasRows = rows.length > 0;
  // The owed charge is ours, so it must not vouch for a history that failed to load.
  const hasFailed = isError && items.length === 0;

  useEffect(() => {
    return () => remove();
  }, [remove]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('subtitle', { deadline: getInvoicingDeadlineLabel() })}</p>
      </div>

      {overdueCharge && <BlockPendingPaymentAlert charge={overdueCharge} />}

      {!hasInvoiceData && (
        <div
          className="flex flex-wrap items-start gap-2.5 rounded-lg border bg-muted p-3.5 sm:flex-nowrap"
          data-testid="payment-history-no-invoice-data-note"
        >
          <ReceiptText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="min-w-[12rem] flex-1 text-sm leading-relaxed">{t('noInvoiceDataNote.message')}</p>
          {areActionsLocked ? (
            <Button className="h-8 shrink-0" disabled size="sm" variant="outline">
              {t('noInvoiceDataNote.action')}
            </Button>
          ) : (
            <Button asChild className="h-8 shrink-0" size="sm" variant="outline">
              <Link to={`/subscriptions/blocks/${blockUuid}?tab=invoicing`}>{t('noInvoiceDataNote.action')}</Link>
            </Button>
          )}
        </div>
      )}

      {isLoading &&
        (isMobile ? (
          <PaymentHistoryMobileListSkeleton />
        ) : (
          <PaymentHistoryTableSkeleton showConcept={false} showSubscription={false} />
        ))}

      {hasFailed && (
        <ErrorState
          description={t('errorDescription')}
          onRetry={() => refetch()}
          retryLabel={t('retry')}
          title={t('errorLoading')}
        />
      )}

      {!isLoading && !hasFailed && !hasRows && (
        <EmptyState description={t('emptyDescription')} title={t('emptyTitle')} />
      )}

      {!isLoading &&
        !hasFailed &&
        hasRows &&
        (isMobile ? (
          <PaymentHistoryMobileList
            blockUuid={blockUuid}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            payments={rows}
            showSubscription={false}
          />
        ) : (
          <PaymentHistoryTable
            blockUuid={blockUuid}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            payments={rows}
            showConcept={false}
            showSubscription={false}
          />
        ))}
    </div>
  );
};
